import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignPortalSyncModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignPortalSync/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignPortalSync/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignPortalSync/FETCH_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignPortalSync/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignPortalSync/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignPortalSync/UPDATE_FAILURE'

export type CampaignPortalSyncModuleState = {
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignPortalSyncModuleState = {
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignPortalSyncs(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignPortalSyncs.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_portal_syncs.json', options)
      .then(({ data }: { data: CampaignPortalSyncModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_PORTAL_SYNC_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignPortalSync(campaignPortalSyncId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignPortalSyncs.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/campaign_portal_syncs/${campaignPortalSyncId}.json`, options)
      .then(({ data }: { data: CampaignPortalSyncModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_PORTAL_SYNC)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function fetchLog(campaignPortalSync: CampaignPortalSyncModel, options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_portal_syncs/${campaignPortalSync.id}/fetch_log.json`, options)
      .then(({ data }: { data: CampaignPortalSyncModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_PORTAL_SYNC)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignPortalSyncModuleState = initialState,
  action: Action = { type: '' },
): CampaignPortalSyncModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
